import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Button, Space, App } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import userAdminService from '../../services/userAdminService';
import passwordResetService from '../../services/passwordResetService';
import UserFormModal from './UserFormModal';
import UserManagementTable from './UserManagementTable';
import {
  authenticatedGet,
  createAuthStateHandler,
  isAuthError,
  buildApiUrl,
  API_ENDPOINTS,
} from '../../utils/authUtils';
import logger from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';

const UserManagement = () => {
  const { message } = App.useApp();
  const {
    currentUser,
    logout,
    isTokenExpired,
    validateSession,
  } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Create auth state handler for consistent error handling (memoized to prevent unnecessary re-renders)
  const authHandler = useMemo(() => createAuthStateHandler(setError, logout), [logout]);

  // Check if user is authorized
  const isAuthorized = currentUser?.role === 'super_admin' || currentUser?.role === 'tenant_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const fetchUsers = useCallback(async () => {
    await authHandler.safeApiCall(async () => {
      setLoading(true);
      setError(null);

      try {
        const userData = await userAdminService.getAllUsers();
        setUsers(userData);
      } catch (err) {
        if (!authHandler.handleAuthError(err)) {
          throw new Error('Failed to fetch users');
        }
      } finally {
        setLoading(false);
      }
    });
  }, [authHandler]);

  const fetchTenants = useCallback(async () => {
    await authHandler.safeApiCall(async () => {
      setError(null);

      const tenantsData = await authenticatedGet(
        buildApiUrl(API_ENDPOINTS.ADMIN.TENANTS),
        authHandler.handleAuthError
      );

      setTenants(tenantsData);
    });
  }, [authHandler]);

  // Validate session on component mount
  useEffect(() => {
    if (!validateSession()) {
      return;
    }

    if (isAuthorized) {
      fetchUsers();
      if (isSuperAdmin) {
        fetchTenants();
      }
    }
  }, [isAuthorized, isSuperAdmin, validateSession, fetchUsers, fetchTenants]);

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserFormOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormOpen(true);
  };

  const handleSubmitUser = async (userData) => {
    // The write itself. Any failure here propagates to UserFormModal, which keeps
    // the form open and shows the API's detail (FE-3-1) — the modal must never
    // close on a write that did not land.
    if (editingUser) {
      // Update existing user
      await passwordResetService.updateUser(editingUser.id || editingUser.email, userData);
    } else {
      // Create new user
      await userAdminService.createUser(userData);
    }

    // The write succeeded. A failure refreshing the list afterwards is a separate,
    // non-fatal problem: letting it reach the modal's catch would report a saved
    // user as a failed save, which is the inverse of the FE-3-1 bug.
    try {
      await fetchUsers();
    } catch (refreshError) {
      logger.error('User saved, but refreshing the user list failed:', refreshError);
      message.warning('User saved, but the list could not be refreshed. Reload to see the change.');
    }

    setUserFormOpen(false);
    setEditingUser(null);
  };

  const handleResetPassword = async (email) => {
    try {
      await passwordResetService.adminResetPassword(email);
      message.success(`Password reset email sent to ${email}`);
    } catch (error) {
      logger.error('Failed to reset password:', error);
      message.error(getApiErrorMessage(error, 'Failed to send the password reset email.'));
    }
  };

  // Row actions (delete, disable / enable): a 401 goes to the session handler,
  // which logs out. Any other failure is reported once, as a toast — not routed
  // through safeApiCall, which also put axios's raw "Request failed with status
  // code 403" in the page banner (FE-5-7).
  const reportRowActionError = (error, fallback) => {
    if (error?.response?.status === 401 || isAuthError(error)) {
      authHandler.handleAuthError(error);
      return;
    }
    message.error(getApiErrorMessage(error, fallback));
  };

  const handleDeleteUser = async (email) => {
    try {
      await userAdminService.deleteUser(email);
    } catch (error) {
      logger.error('Failed to delete user:', error);
      reportRowActionError(error, 'Failed to delete user.');
      return;
    }

    message.success('User deleted successfully');

    try {
      await fetchUsers();
    } catch (refreshError) {
      logger.error('User deleted, but refreshing the user list failed:', refreshError);
      message.warning('User deleted, but the list could not be refreshed. Reload to see the change.');
    }
  };

  // REQ-005 FE-5-6: disable / enable (PATCH /admin/users/{email}/status)
  const handleSetUserStatus = async (email, disabled) => {
    try {
      await userAdminService.setUserStatus(email, disabled);
    } catch (error) {
      logger.error('Failed to change user status:', error);
      reportRowActionError(error, disabled ? 'Failed to disable user.' : 'Failed to enable user.');
      return;
    }

    message.success(disabled ? `${email} has been disabled` : `${email} has been enabled`);

    try {
      await fetchUsers();
    } catch (refreshError) {
      logger.error('User status changed, but refreshing the user list failed:', refreshError);
      message.warning('Status changed, but the list could not be refreshed. Reload to see the change.');
    }
  };

  // Session expired state
  if (isTokenExpired) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>⚠️ Session Expired</h2>
        <p>Your session has expired. Please log in again to continue.</p>
        <Space>
          <Button
            type="primary"
            onClick={() => window.location.href = '/login'}
          >
            Log In Again
          </Button>
          <Button onClick={() => window.location.href = '/'}>
            Go to Home
          </Button>
        </Space>
      </div>
    );
  }

  // Check authorization
  if (!isAuthorized) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 56px)', overflow: 'auto', background: 'var(--rlc-surface-sunken)' }}>
      <div style={{ padding: '24px' }}>
      {/* Header with Create User Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24
      }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--rlc-surface-inverse)' }}>User Management</h2>
          <p style={{ margin: '4px 0 0 0', color: 'var(--rlc-text-secondary)' }}>
            Manage user accounts and permissions
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleCreateUser}
        >
          Create User
        </Button>
      </div>

      {/* Global Error Display */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'var(--rlc-state-error-bg)',
          border: '1px solid var(--rlc-state-error-bg)',
          borderRadius: '4px',
          marginBottom: 16,
          color: 'var(--rlc-state-error)'
        }}>
          {error}
          <Button
            type="link"
            size="small"
            onClick={() => setError(null)}
            style={{ float: 'right' }}
          >
            ×
          </Button>
        </div>
      )}

      {/* User Table */}
      <UserManagementTable
        users={users}
        loading={loading}
        currentUserEmail={currentUser?.email}
        currentUser={currentUser}
        tenants={tenants}
        onEdit={handleEditUser}
        onResetPassword={handleResetPassword}
        onDelete={handleDeleteUser}
        onSetStatus={handleSetUserStatus}
      />

      {/* Create/Edit User Modal */}
      <UserFormModal
        open={userFormOpen}
        onClose={() => {
          setUserFormOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleSubmitUser}
        user={editingUser}
        tenants={tenants}
        isSuperAdmin={isSuperAdmin}
        currentUser={currentUser}
      />
    </div>
  </div>
  );
};
export default UserManagement;
